import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { API_ENDPOINTS, getApiUrl } from '../utils/api';
import { parseSessionExpiryTime } from '../utils/sessionUtils';

const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // Refresh every 6 hours

/**
 * Hook that periodically refreshes the session token to keep the Redis
 * accessSecret alive and prevent premature session expiry.
 *
 * It calls the backend refresh-token endpoint which re-generates the JWT,
 * accessSecret, and resets the Redis TTL.
 */
const useSessionRefresh = (
  userType: 'Administrator' | 'Merchant',
  onSessionLost?: () => void,
) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshSession = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    const accessSecret = localStorage.getItem('accessSecret');

    if (!accessToken || !accessSecret) return;

    const endpoint =
      userType === 'Administrator'
        ? API_ENDPOINTS.REFRESH_TOKEN_ADMIN
        : API_ENDPOINTS.REFRESH_TOKEN_MERCHANT;

    try {
      const timestamp = Date.now();
      const payload = '{}';
      const hmacSignature = CryptoJS.HmacSHA256(
        `${timestamp}:${payload}`,
        accessSecret,
      ).toString(CryptoJS.enc.Hex);

      // Generate nonce
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);
      const nonce = Array.from(array, (byte) =>
        byte.toString(16).padStart(2, '0'),
      ).join('');

      const response = await axios.post(
        getApiUrl(endpoint),
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY || '',
            Authorization: `Bearer ${accessToken}`,
            'x-hmac-signature': hmacSignature,
            'x-request-timestamp': timestamp.toString(),
            'x-nonce-string': nonce,
          },
        },
      );

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;

        if (data.accessToken) {
          localStorage.setItem('accessToken', data.accessToken);
        }
        if (data.accessSecret) {
          localStorage.setItem('accessSecret', data.accessSecret);
        }
        if (data.sessionExpiryTime) {
          const expiryTimestamp = parseSessionExpiryTime(
            data.sessionExpiryTime,
          );
          localStorage.setItem('sessionExpiryTime', expiryTimestamp.toString());
        }
      }
    } catch (error: any) {
      const status = error.response?.status || error.response?.statusCode;
      // If refresh itself fails with auth error, session is truly dead
      if (status === 401 || status === 403) {
        onSessionLost?.();
      }
    }
  }, [userType, onSessionLost]);

  useEffect(() => {
    // Do an initial refresh shortly after mount (30 seconds) to reset the TTL
    const initialTimeout = setTimeout(refreshSession, 30_000);

    // Then refresh periodically
    intervalRef.current = setInterval(refreshSession, REFRESH_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refreshSession]);

  return { refreshSession };
};

export default useSessionRefresh;
